<template>
  <component
    :is="tag"
    class="play-btn"
    :class="[`play-btn--${variant}`, `play-btn--${size}`, { 'play-btn--block': block, 'play-btn--icon': icon }]"
    :type="tag === 'button' ? type : null"
    :disabled="tag === 'button' ? disabled : null"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
       On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
    <span
      v-if="$slots.icon"
      class="play-btn__icon"
    ><slot name="icon" /></span>
    <span class="play-btn__label"><slot /></span>
  </component>
</template>

<script>
/**
 * PlayButton — pattern reference for the Play system component layer.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors, so a
 * palette swap requires zero changes here. Replaces bootstrap-vue's
 * <b-button> in the rebrand.
 *
 * Renders as <button> by default, or <a>/<router-link> via `tag` so it
 * works for both actions and links. All native attrs/listeners pass through.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` in the template is the only Vue 2-ism
 * here. In Vue 3 $listeners is removed (merged into $attrs); drop that line
 * and v-bind="$attrs" keeps forwarding listeners. The props/slots contract is
 * already Vue 3-safe.
 */
export default {
  name: 'PlayButton',
  inheritAttrs: false,
  props: {
    /** Visual role: primary CTA, lime highlight, violet, outlined ghost, or
     *  borderless plain (quiet icon/utility control — inherits its color) */
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'highlight', 'secondary', 'ghost', 'plain'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    /** Element to render: 'button' | 'a' | 'router-link' */
    tag: {
      type: String,
      default: 'button',
    },
    type: {
      type: String,
      default: 'button',
    },
    block: {
      type: Boolean,
      default: false,
    },
    /** Square icon-only button (equal padding, no wide horizontal padding) */
    icon: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
.play-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: var(--font-display);
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  transition:
    transform var(--dur) var(--ease-bounce),
    box-shadow var(--dur) var(--ease-bounce);
  will-change: transform;
  -webkit-tap-highlight-color: transparent;

  &__label { display: inline-block; }
  &__icon {
    display: inline-flex;
    align-items: center;
    font-size: 1.1em;
  }

  /* ── Sizes ──
     Fixed heights (not padding-derived): every variant of the same size is
     exactly the same height, ghost's 2.5px border included — footer pairs like
     ghost Close + primary Save always align with no local overrides. */
  &--sm { height: 32px; padding: 0 16px; font-size: 13.5px; border-radius: var(--radius-sm); }
  &--md { height: 41px; padding: 0 24px; font-size: 15px; }
  &--lg { height: 49px; padding: 0 30px; font-size: 17px;  border-radius: var(--radius-lg); }

  &--block { display: flex; width: 100%; }

  /* ── Icon-only (square) ── */
  &--icon {
    gap: 0;
    padding: 0;
    &.play-btn--sm { width: 32px; }
    &.play-btn--md { width: 41px; }
    &.play-btn--lg { width: 49px; }
    .play-btn__label { display: inline-flex; }
  }

  /* ── Press feel (shared) ── */
  &:active {
    transform: translateY(3px);
    box-shadow: var(--shadow-press);
  }

  /* No focus ring lingering after a click — Safari flags a mouse click as
     :focus-visible, so the ink outline would stick to the button (see the
     CLAUDE.md rule). Hover/press motion is the affordance. */
  &:focus,
  &:focus-visible {
    outline: none;
  }

  &:disabled,
  &.disabled {
    opacity: .55;
    pointer-events: none;
  }

  /* ── Variants ──
     One shared motion (the CLAUDE.md button-animation rule, modeled on the
     ghost/Photo trigger): printed shadow 4px at rest → 6px + 1px lift on
     hover → 2px press on active. Colored variants use their role's -strong
     slot for the shadow; no big colored glows (quiet-confidence dashboard). */
  /* NB: each variant's :active must redeclare the press transform — while the
     mouse is down :hover still applies, and the variant hover selector would
     otherwise out-rank the shared `.play-btn:active` and keep the -1px lift. */
  &--primary {
    background: var(--c-primary);
    color: var(--c-primary-on);
    box-shadow: 0 4px 0 var(--c-primary-strong);
    &:hover { box-shadow: 0 6px 0 var(--c-primary-strong); transform: translateY(-1px); }
    &:active { box-shadow: 0 2px 0 var(--c-primary-strong); transform: translateY(3px); }
  }

  &--highlight {
    background: var(--c-highlight);
    color: var(--c-highlight-on);
    box-shadow: 0 4px 0 var(--c-highlight-strong);
    &:hover { box-shadow: 0 6px 0 var(--c-highlight-strong); transform: translateY(-1px); }
    &:active { box-shadow: 0 2px 0 var(--c-highlight-strong); transform: translateY(3px); }
  }

  &--secondary {
    background: var(--c-secondary);
    color: var(--c-secondary-on);
    box-shadow: 0 4px 0 var(--c-secondary-strong);
    &:hover { box-shadow: 0 6px 0 var(--c-secondary-strong); transform: translateY(-1px); }
    &:active { box-shadow: 0 2px 0 var(--c-secondary-strong); transform: translateY(3px); }
  }

  &--ghost {
    background: var(--surface-card);
    color: var(--text-strong);
    border: var(--border-w) solid var(--border-ink);
    box-shadow: var(--shadow-hard-sm);
    &:hover { box-shadow: var(--shadow-hard); transform: translateY(-1px); }
    &:active { box-shadow: var(--shadow-press); transform: translateY(3px); }
  }

  /* Quiet utility control (locale switcher, toolbar toggles): no box, no
     printed shadow. Role-neutral — color comes from the parent via `inherit`,
     so it can pair with a sibling icon button (e.g. the theme toggle). */
  &--plain {
    background: transparent;
    color: inherit;
    &:hover { opacity: .7; }
    &:active { transform: none; box-shadow: none; }
  }
}
</style>
